// LAP-550 — Card Alternatifs : header + segmenter + 5 vues (vins / montres / crypto / art / voitures)
import React, { useEffect, useState } from 'react'
import { Box, Grid, Stack, ButtonBase } from '@mui/material'
import { useTranslation } from 'react-i18next'
import AddOpportunityAction from '../../_shared/AddOpportunityAction'
import CardHeader from '../../_shared/CardHeader'
import Delta from '../../_shared/Delta'
import { invTheme } from '../../_shared/theme'

function AltCardItem({ children }) {
  return (
    <Box
      sx={{
        display: 'flex',
        p: 0.75,
        borderRadius: 1.5,
        backgroundColor: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.05)',
      }}
    >
      {children}
    </Box>
  )
}

function AltImage({ src, ratio = 1, height = 70 }) {
  return (
    <Box
      sx={{
        width: ratio === 1 ? height : height * ratio,
        height,
        flexShrink: 0,
        mr: 1,
        borderRadius: 1,
        overflow: 'hidden',
        backgroundColor: 'rgba(255,255,255,0.04)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
    </Box>
  )
}

export default function AlternativeInvestmentCard({ result = [] }) {
  const { t } = useTranslation('layout')
  const [viewData, setViewData] = useState({})
  useEffect(() => {
    if (result.length > 0) setViewData(result[4] || result[0])
  }, [result])
  const handelbutton = (type) => {
    setViewData(result.find((r) => r.type === type))
  }

  return (
    <Box display="flex" flexDirection="column" height="100%">
      <CardHeader
        icon="/images/alternatif.png"
        title={t('investments.markeet.alternative.title')}
        subtitle={viewData?.title}
      />
      <Box
        mt={2}
        height="100%"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {viewData?.button && (
          <Stack
            direction="row"
            spacing={0.5}
            sx={{
              mb: 1.5,
              p: 0.25,
              borderRadius: 999,
              backgroundColor: 'rgba(255,255,255,0.04)',
              alignSelf: 'flex-start',
            }}
          >
            {viewData.button.map((b, idx) => {
              const active = b.type === viewData?.type
              return (
                <ButtonBase
                  key={idx}
                  onClick={() => handelbutton(b.type)}
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    fontSize: 11,
                    fontWeight: 600,
                    borderRadius: 999,
                    color: active ? '#1a0d2e' : 'rgba(255,255,255,0.65)',
                    backgroundColor: active ? invTheme.accents.alternative : 'transparent',
                    textTransform: 'uppercase',
                    letterSpacing: 0.4,
                    '&:hover': {
                      backgroundColor: active ? invTheme.accents.alternative : 'rgba(255,255,255,0.06)',
                    },
                  }}
                >
                  {b.name}
                </ButtonBase>
              )
            })}
          </Stack>
        )}
        <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
          {viewData?.type === 'vins_alternatifs' && (
            <>
              <Grid container spacing={1.5}>
                {viewData?.data?.map((item, index) => (
                  <Grid item sm={6} key={index}>
                    <AltCardItem>
                      <AltImage src={item.image} ratio={0.55} />
                      <Stack sx={{ flexGrow: 1, alignItems: 'center', justifyContent: 'center' }}>
                        <Box sx={{ fontWeight: 600, fontSize: 12, textAlign: 'center' }}>{item.name}</Box>
                        <Delta value={item.variation_1_an} unit="%" />
                        <Box sx={{ fontWeight: 700, mt: 0.5 }}>{item.price} €</Box>
                        <AddOpportunityAction
                          initialValues={{
                            name: item.name,
                            price: item.price,
                            value_change_pct: item.variation_1_an,
                          }}
                        />
                      </Stack>
                    </AltCardItem>
                  </Grid>
                ))}
              </Grid>
              <Box mt={2}>
                <Box sx={invTheme.subtitle}>{t('investments.markeet.alternative.index')}</Box>
                <Box mt={0.75}>
                  {viewData?.indice?.map((item, index) => (
                    <Box
                      key={index}
                      display="flex"
                      sx={{
                        py: 0.5,
                        borderBottom: '1px solid rgba(255,255,255,0.05)',
                        '&:last-of-type': { borderBottom: 'none' },
                      }}
                    >
                      <Box sx={{ fontSize: 13 }}>{item.name}</Box>
                      <Box sx={{ ml: 'auto' }}>
                        <Delta value={item.variation_1_an} unit="%" />
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            </>
          )}
          {viewData?.type === 'montre_alternatifs' && (
            <Grid container spacing={1.5}>
              {viewData?.data?.map((item, index) => (
                <Grid key={index} item sm={6}>
                  <AltCardItem>
                    <AltImage src={item.image} ratio={1} />
                    <Stack sx={{ flexGrow: 1, justifyContent: 'center' }}>
                      <Box sx={{ fontSize: 12, color: 'rgba(255,255,255,0.6)' }}>{item.marque}</Box>
                      <Box sx={{ fontWeight: 600, fontSize: 13 }}>{item.modele}</Box>
                      <Box display="flex" alignItems="center" mt={0.5}>
                        <Box sx={{ fontWeight: 700 }}>{item.price} €</Box>
                        <Box sx={{ ml: 'auto' }}>
                          <Delta value={item.variation_1_an} unit="%" />
                        </Box>
                      </Box>
                      <AddOpportunityAction
                        initialValues={{
                          name: `${item.marque} ${item.modele}`,
                          price: item.price,
                          industry: item.marque,
                          value_change_pct: item.variation_1_an,
                        }}
                      />
                    </Stack>
                  </AltCardItem>
                </Grid>
              ))}
            </Grid>
          )}
          {viewData?.type === 'crypto_asset' && (
            <Grid container spacing={1.5}>
              {viewData?.data?.map((item, index) => (
                <Grid key={index} item sm={6}>
                  <AltCardItem>
                    <AltImage src={item.image} ratio={1} height={64} />
                    <Stack sx={{ flexGrow: 1, justifyContent: 'center' }}>
                      <Box sx={{ fontWeight: 700, fontSize: 13 }}>{item.symbol}</Box>
                      <Box sx={{ fontSize: 11, color: 'rgba(255,255,255,0.55)' }}>{item.name}</Box>
                      <Box display="flex" alignItems="center" mt={0.5}>
                        <Box sx={{ fontWeight: 600 }}>{item.price}</Box>
                        <Box sx={{ ml: 'auto' }}>
                          <Delta value={item.changePercentage} unit="%" />
                        </Box>
                      </Box>
                      <AddOpportunityAction
                        initialValues={{
                          name: item.name,
                          price: item.price,
                          market_id: (item.symbol || '').slice(0, 5),
                          value_change_pct: item.changePercentage,
                        }}
                      />
                    </Stack>
                  </AltCardItem>
                </Grid>
              ))}
            </Grid>
          )}
          {viewData?.type === 'art_alternatifs' && (
            <Grid container spacing={1.5}>
              {viewData?.data?.map((item, index) => (
                <Grid key={index} item sm={6}>
                  <AltCardItem>
                    <AltImage src={item.image} ratio={0.7} height={80} />
                    <Stack sx={{ flexGrow: 1, justifyContent: 'center' }}>
                      <Box sx={{ fontWeight: 600, fontSize: 13 }}>{item.modele}</Box>
                      <Box sx={{ fontSize: 12, color: 'rgba(255,255,255,0.7)' }}>{item.artiste}</Box>
                      <Box sx={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>{item.subtype}</Box>
                      <Box sx={{ fontWeight: 700, mt: 0.5, color: '#cfe1ff' }}>{item.price} €</Box>
                      <AddOpportunityAction
                        initialValues={{
                          name: item.modele,
                          price: item.price,
                          industry: item.artiste,
                          sector: item.subtype,
                        }}
                      />
                    </Stack>
                  </AltCardItem>
                </Grid>
              ))}
            </Grid>
          )}
          {viewData?.type === 'voiture_alternatifs' && (
            <Stack spacing={1.25}>
              {viewData?.data?.map((item, index) => (
                <AltCardItem key={index}>
                  <AltImage src={item.image} ratio={1.4} height={70} />
                  <Stack sx={{ flexGrow: 1, justifyContent: 'center' }}>
                    <Box sx={{ fontWeight: 600, fontSize: 14 }}>
                      {item.marque} {item.modele}
                    </Box>
                    <Box sx={{ fontSize: 11, color: 'rgba(255,255,255,0.55)' }}>{item.annee}</Box>
                    <Box sx={{ fontWeight: 700, color: '#cfe1ff', mt: 0.5 }}>{item.price} €</Box>
                    <AddOpportunityAction
                      initialValues={{
                        name: `${item.marque} ${item.modele}`,
                        price: item.price,
                        description: `${item.annee}`,
                      }}
                    />
                  </Stack>
                </AltCardItem>
              ))}
            </Stack>
          )}
        </Box>
      </Box>
    </Box>
  )
}
